import axiosClient from "../axiosClient";
import {
  CACHE_KEYS,
  SESSION_CACHE_TTL,
  cachedRequest,
} from "../../utils/apiCache";

function withBearer() {
  const bearerTokenHeader = sessionStorage.getItem("accessToken");
  axiosClient.defaults.headers.common["Authorization"] =
    `Bearer ${bearerTokenHeader}`;
  return axiosClient;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function unwrap(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (
    payload.easters == null &&
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return payload;
}

export function getEasters(options = {}) {
  return cachedRequest(
    CACHE_KEYS.easters,
    () => withBearer().get("/economy/easters"),
    { ttl: SESSION_CACHE_TTL, force: options.force === true },
  );
}

export function normalizeEasters(payload) {
  const root = unwrap(payload);
  if (!root || root.success === false || payload?.success === false) return null;
  if (root.easters == null || root.easters === "") return null;

  const easters = toNumber(root.easters, NaN);
  if (!Number.isFinite(easters) || easters < 0) return null;

  return {
    success: true,
    easters,
  };
}

export function readEasterBalance() {
  const stored = sessionStorage.getItem("easterBalance");
  const n = Number(stored);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function creditEarnedEasters(amount) {
  const awarded = Math.max(0, toNumber(amount, 0));
  if (!awarded) return readEasterBalance();
  return persistEasterBalance(readEasterBalance() + awarded);
}

export function balanceAfterReward(currentBalance, eggsAwarded, serverBalance) {
  const current = Math.max(0, toNumber(currentBalance, 0));
  const awarded = Math.max(0, toNumber(eggsAwarded, 0));
  const earned = current + awarded;
  if (serverBalance == null || serverBalance === "") return earned;

  const server = Number(serverBalance);
  if (!Number.isFinite(server) || server < 0) return earned;
  return server >= earned ? server : earned;
}

export function persistEasterBalance(easters) {
  const balance = Math.max(0, toNumber(easters, 0));
  let profile = {};
  try {
    profile = JSON.parse(sessionStorage.getItem("profileData") || "{}") || {};
  } catch {
    profile = {};
  }

  const next = {
    ...profile,
    easters: balance,
    eggs: balance,
    easter_eggs: balance,
    easterEggs: balance,
  };

  sessionStorage.setItem("profileData", JSON.stringify(next));
  sessionStorage.setItem("easterBalance", String(balance));
  window.dispatchEvent(new Event("profileUpdated"));
  return balance;
}

/**
 * Refresh egg wallet for chrome chips.
 * @param {{ force?: boolean }} [options] force=true bypasses TTL (after claims).
 * Default uses session cache so Layout route changes do not re-hit the API.
 */
export async function refreshEasterWallet(options = {}) {
  try {
    return await loadEasterBalance({ force: options.force === true });
  } catch (error) {
    console.error("Easter wallet refresh failed:", error);
    return null;
  }
}

export async function loadEasterBalance(options = {}) {
  // Force is handled inside getEasters/cachedRequest (deduped). Do not
  // pre-invalidate here — that dropped in-flight requests and caused
  // duplicate /economy/easters calls from Layout + page mounts.
  const response = await getEasters(options);
  const wallet = normalizeEasters(response?.data);
  const staleKeys = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (
      key === "ga_pending_egg_credit" ||
      key === "ga_pending_egg_baseline" ||
      (key && key.startsWith("ga_challenge_done_"))
    ) {
      staleKeys.push(key);
    }
  }
  staleKeys.forEach((key) => sessionStorage.removeItem(key));
  if (wallet) persistEasterBalance(wallet.easters);
  return wallet;
}
